import { ArrowRight, Check, Smartphone, Zap, Search, Sparkles, Star } from "lucide-react";
import Reveal from "./Reveal";
import { useT } from "../../i18n/I18nContext";

const TRUST_ICONS = [Smartphone, Zap, Search, Sparkles];

export const Hero = () => {
  const t = useT();
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 lg:pt-44 lg:pb-28 overflow-hidden bg-[#F8FAFC]"
    >
      {/* Background decorations */}
      <div className="absolute inset-0 uv-grid-bg pointer-events-none" aria-hidden="true" />
      <div className="uv-orb -top-32 -right-20 w-[320px] h-[320px] sm:w-[520px] sm:h-[520px] bg-[#2563EB]/15 sm:bg-[#2563EB]/10" aria-hidden="true" />
      <div className="uv-orb top-1/3 -left-32 w-[280px] h-[280px] sm:w-[420px] sm:h-[420px] bg-[#10B981]/10" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-10 items-center">
          {/* Copy */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-1.5 shadow-sm">
                <span className="inline-flex w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                <span className="text-[11px] sm:text-xs font-semibold tracking-wider text-slate-600 uppercase">
                  {t.hero.pill}
                </span>
              </div>
            </Reveal>

            <Reveal delay={80}>
              <h1
                data-testid="hero-headline"
                className="mt-5 sm:mt-6 font-extrabold text-[#0A2342] text-[34px] leading-[1.08] sm:text-5xl lg:text-6xl tracking-tight"
              >
                {t.hero.headlineA}{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">{t.hero.headlineB}</span>
                  <span
                    className="uv-underline-anim absolute left-0 right-0 bottom-1 h-3 bg-[#10B981]/25 -z-0 rounded-sm"
                    aria-hidden="true"
                  />
                </span>{" "}
                {t.hero.headlineC}
              </h1>
            </Reveal>

            <Reveal delay={160}>
              <p
                data-testid="hero-subheadline"
                className="mt-5 sm:mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed"
              >
                {t.hero.subheadline}
              </p>
            </Reveal>

            <Reveal delay={220}>
              <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row gap-3">
                <a
                  href="#contact"
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-[#2563EB]/25 hover:bg-[#1D4ED8] hover:gap-3 active:scale-[0.98] transition-all"
                >
                  {t.hero.ctaPrimary}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </a>
                <a
                  href="#portfolio"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-semibold text-[#0A2342] ring-1 ring-inset ring-slate-300 hover:bg-slate-50 active:scale-[0.98] transition-all"
                >
                  {t.hero.ctaSecondary}
                </a>
              </div>
            </Reveal>

            {/* Trust indicators */}
            <Reveal delay={300}>
              <ul
                data-testid="hero-trust-indicators"
                className="mt-8 sm:mt-10 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-3 max-w-2xl"
              >
                {t.hero.trust.map((label, i) => {
                  const Icon = TRUST_ICONS[i] || Check;
                  return (
                    <li
                      key={label}
                      className="flex items-center gap-2 text-sm font-medium text-slate-700"
                    >
                      <span className="inline-flex w-7 h-7 shrink-0 items-center justify-center rounded-full bg-[#10B981]/10 text-[#059669]">
                        <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
                      </span>
                      <span className="leading-tight">{label}</span>
                    </li>
                  );
                })}
              </ul>
            </Reveal>
          </div>

          {/* Visual */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <Reveal delay={150}>
              <div className="relative mx-auto max-w-[420px] sm:max-w-none">
                {/* Decorative gradient blob behind image (visible on mobile too) */}
                <div
                  className="absolute -inset-3 sm:-inset-4 rounded-[2rem] bg-gradient-to-tr from-[#2563EB]/20 via-[#10B981]/15 to-transparent blur-2xl opacity-70 pointer-events-none"
                  aria-hidden="true"
                />

                {/* Floating accent stat cards — visible on mobile too */}
                <div className="uv-float-slow absolute -top-3 -left-2 sm:-top-6 sm:-left-6 z-20 flex items-center gap-2.5 sm:gap-3 bg-white rounded-2xl shadow-xl ring-1 ring-slate-100 px-3 py-2 sm:px-4 sm:py-3">
                  <div className="flex -space-x-2">
                    <span className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-[#0A2342] text-white text-[10px] sm:text-xs font-bold flex items-center justify-center ring-2 ring-white">RM</span>
                    <span className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-[#2563EB] text-white text-[10px] sm:text-xs font-bold flex items-center justify-center ring-2 ring-white">DR</span>
                    <span className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-[#10B981] text-white text-[10px] sm:text-xs font-bold flex items-center justify-center ring-2 ring-white">JB</span>
                  </div>
                  <div className="leading-tight">
                    <p className="text-[10px] sm:text-xs font-semibold text-slate-500">
                      {t.hero.recentClients}
                    </p>
                    <p className="text-xs sm:text-sm font-bold text-[#0A2342]">
                      {t.hero.recentClientsSub}
                    </p>
                  </div>
                </div>

                <div className="uv-float absolute -bottom-3 right-2 sm:-bottom-6 sm:-right-2 z-20 flex items-center gap-2 bg-white rounded-2xl shadow-xl ring-1 ring-slate-100 px-3 py-2 sm:px-4 sm:py-3">
                  <span className="inline-flex w-8 h-8 sm:w-9 sm:h-9 items-center justify-center rounded-full bg-[#10B981]/10 text-[#059669]">
                    <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-[#10B981] text-[#10B981]" />
                  </span>
                  <div className="leading-tight">
                    <p className="text-xs sm:text-sm font-bold text-[#0A2342]">4.9 / 5.0</p>
                    <p className="text-[10px] sm:text-xs font-medium text-slate-500">
                      {t.hero.satisfaction}
                    </p>
                  </div>
                </div>

                <div className="relative rounded-3xl overflow-hidden ring-1 ring-slate-200/80 shadow-2xl shadow-[#0A2342]/20">
                  <img
                    src="https://images.pexels.com/photos/251225/pexels-photo-251225.jpeg"
                    alt="Modern workspace with multiple monitors showing a web design project"
                    className="w-full h-[260px] sm:h-[380px] lg:h-[520px] object-cover"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#0A2342]/40 via-transparent to-transparent" />

                  {/* Subtle browser-style chrome at top of image for "live website" feel */}
                  <div className="absolute top-0 inset-x-0 bg-gradient-to-b from-[#0A2342]/40 to-transparent h-10 flex items-center gap-1.5 px-4">
                    <span className="w-2 h-2 rounded-full bg-red-400/80" />
                    <span className="w-2 h-2 rounded-full bg-yellow-400/80" />
                    <span className="w-2 h-2 rounded-full bg-green-400/80" />
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
